import { ActionPlanTemplateEdit } from "../ActionPlanTemplates/ActionPlanTemplateEdit";
import { lazy } from "react";
import { Route } from "react-router";
import { AppParamsSettings } from "../AppParams/AppParam";
import { AuditLogPage } from "../AuditLog/AuditLogPage";
import { ApplicationCreate } from "../Applications/ApplicationCreate";
import { ApplicationEdit } from "../Applications/ApplicationEdit";
import { ActionPlanTemplateDetail } from "../ActionPlanTemplates/ActionPlanTemplateDetail";
import { ActionPlanTemplates } from "../ActionPlanTemplates/ActionPlanTemplates";
import { ActionPlanTemplatesProtection } from "./ActionPlanTemplatesProtection";
import { Campaigns } from "../Campaigns";
import { ConsentGovernanceSettings } from "../Consent/ConsentGovernanceSettings";
import { SingleSignOnSettings } from "../SingleSignOn/SingleSignOnSettings";
import { CsrThemeCreate } from "../CsrTheme/Create";
import { CsrThemeEdit } from "../CsrTheme/Edit";
import { CsrTheme } from "../CsrTheme/List";
import { CustomThemesList } from "../CustomTheme/CustomThemesList";
import { DynaBlockEditor } from "../Extensions/DynaBlockEditor";
import { DynaBlockManager } from "../Extensions/DynaBlockManager";
import FeatureActivation from "../FeatureSwitch/FeatureActivation";
import Integrations from "../Integrations";
import { LanguageManagement } from "../Languages/LanguageManagement";
import { MenuManagement } from "../MenuManagement/MenuManagement";
import PermissionList from "../Permissions/PermissionList";
import { RoleCreate } from "../Roles/Create";
import { RoleEdit } from "../Roles/Edit";
import { RolesList } from "../Roles/List";
import { ScoresDisplayed } from "../ScoresDisplayed/ScoresDisplayed";
import ThemePage from "../Theme/ThemePage";
import { TranslationManagement } from "../Translations";
import { SettingsRouteBoundary } from "./SettingsRouteBoundary";
import { ApplicationListLazy } from "./lazySettingsPages";
import { FeatureSwitchProtection } from "./FeatureSwitchProtection";
import { APP_INTEGRATION_FEATURE_SWITCH } from "src/api/client-api/applications/constants";
import { CONSENT_GOVERNANCE_FEATURE_SWITCH } from "src/api/client-api/consent/constants";
import { AuditLogProtection } from "./AuditLogProtection";
import { CampaignsProtection } from "./CampaignsProtection";
import { NavigationMenuProtection } from "./NavigationMenuProtection";
import { RolesProtection } from "./RolesProtection";
import { DynaBlocksProtection } from "./DynaBlocksProtection";
import { PlatformAdminProtection } from "./PlatformAdminProtection";
import { LocaleManagementProtection } from "./LocaleManagementProtection";
import { PermissionsProtection } from "./PermissionsProtection";
import { ScoresProtection } from "./ScoresProtection";
import { CustomThemeProtection } from "./CustomThemeProtection";
import { CREATE, MODIFY, VIEW } from "src/context/FoodpilotContext";

const SettingsHomeLazy = lazy(() => import("../SettingsHome"));
const CustomThemeEditorLazy = lazy(() =>
    import("../CustomTheme/CustomThemeEditor").then((module) => ({ default: module.CustomThemeEditor })),
);

export const configurationRoutes = () => (
    <>
        <Route
            path="configuration"
            element={
                <SettingsRouteBoundary>
                    <SettingsHomeLazy />
                </SettingsRouteBoundary>
            }
        />
        <Route
            path="configuration/:sectionId"
            element={
                <SettingsRouteBoundary>
                    <SettingsHomeLazy />
                </SettingsRouteBoundary>
            }
        />
        <Route element={<PlatformAdminProtection />}>
            <Route path="configuration/platform-setup/modules" element={<FeatureActivation />} />
            <Route path="configuration/platform-setup/application-parameters" element={<AppParamsSettings />} />
        </Route>
        <Route element={<ScoresProtection />}>
            <Route path="configuration/platform-setup/scores" element={<ScoresDisplayed />} />
        </Route>
        <Route element={<NavigationMenuProtection />}>
            <Route path="configuration/platform-setup/navigation" element={<MenuManagement />} />
        </Route>
        <Route element={<CampaignsProtection />}>
            <Route path="configuration/platform-setup/campaigns" element={<Campaigns />} />
        </Route>
        <Route element={<ActionPlanTemplatesProtection />}>
            <Route
                path="configuration/platform-setup/action-plan-templates/:templateId/edit"
                element={<ActionPlanTemplateEdit />}
            />
            <Route path="configuration/platform-setup/action-plan-templates" element={<ActionPlanTemplates />} />
            <Route
                path="configuration/platform-setup/action-plan-templates/:templateId"
                element={<ActionPlanTemplateDetail />}
            />
        </Route>
        <Route path="configuration/access-permissions/roles">
            <Route element={<RolesProtection action={VIEW} />}>
                <Route index element={<RolesList />} />
            </Route>
            <Route element={<RolesProtection action={CREATE} />}>
                <Route path="create" element={<RoleCreate />} />
            </Route>
            <Route element={<RolesProtection action={MODIFY} />}>
                <Route path=":roleCode/edit" element={<RoleEdit />} />
            </Route>
        </Route>
        <Route element={<PermissionsProtection />}>
            <Route path="configuration/access-permissions/permissions">
                <Route index element={<PermissionList />} />
            </Route>
        </Route>
        <Route element={<PlatformAdminProtection />}>
            <Route element={<FeatureSwitchProtection featureSwitch={CONSENT_GOVERNANCE_FEATURE_SWITCH} />}>
                <Route
                    path="configuration/access-permissions/consent-governance"
                    element={<ConsentGovernanceSettings />}
                />
            </Route>
        </Route>
        <Route element={<PlatformAdminProtection />}>
            <Route path="configuration/access-permissions/single-sign-on" element={<SingleSignOnSettings />} />
        </Route>
        <Route
            path="configuration/access-permissions/audit-log"
            element={
                <AuditLogProtection>
                    <AuditLogPage />
                </AuditLogProtection>
            }
        />
        <Route element={<PlatformAdminProtection />}>
            <Route path="configuration/customization-languages/branding" element={<ThemePage />} />
            <Route path="configuration/customization-languages/branding/csr-theme">
                <Route index element={<CsrTheme />} />
                <Route path="create" element={<CsrThemeCreate />} />
                <Route path=":csrThemeId/edit" element={<CsrThemeEdit />} />
            </Route>
        </Route>
        <Route element={<CustomThemeProtection action={VIEW} />}>
            <Route path="configuration/customization-languages/branding/custom-themes">
                <Route index element={<CustomThemesList />} />
            </Route>
            <Route element={<CustomThemeProtection action={CREATE} />}>
                <Route
                    path="configuration/customization-languages/branding/custom-theme"
                    element={
                        <SettingsRouteBoundary>
                            <CustomThemeEditorLazy />
                        </SettingsRouteBoundary>
                    }
                />
            </Route>
            <Route element={<CustomThemeProtection action={MODIFY} />}>
                <Route
                    path="configuration/customization-languages/branding/custom-theme/edit/:themeId"
                    element={
                        <SettingsRouteBoundary>
                            <CustomThemeEditorLazy />
                        </SettingsRouteBoundary>
                    }
                />
            </Route>
        </Route>
        <Route element={<DynaBlocksProtection action={VIEW} />}>
            <Route path="configuration/customization-languages/app-blocks" element={<DynaBlockManager />} />
            <Route element={<DynaBlocksProtection action={MODIFY} />}>
                <Route
                    path="configuration/customization-languages/app-blocks/:extensionId"
                    element={<DynaBlockEditor />}
                />
            </Route>
        </Route>
        <Route element={<LocaleManagementProtection />}>
            <Route path="configuration/customization-languages/available-languages" element={<LanguageManagement />} />
        </Route>
        <Route element={<PlatformAdminProtection />}>
            <Route
                path="configuration/customization-languages/translation-overrides"
                element={<TranslationManagement />}
            />
            <Route path="configuration/connections/integrations" element={<Integrations />} />
            <Route element={<FeatureSwitchProtection featureSwitch={APP_INTEGRATION_FEATURE_SWITCH} />}>
                <Route path="configuration/connections/applications">
                    <Route
                        index
                        element={
                            <SettingsRouteBoundary>
                                <ApplicationListLazy />
                            </SettingsRouteBoundary>
                        }
                    />
                    <Route path=":id/edit" element={<ApplicationEdit />} />
                    <Route path="create" element={<ApplicationCreate />} />
                </Route>
            </Route>
        </Route>
    </>
);
